import React, { useContext, useState } from 'react';
import { useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    InputAdornment,
    Avatar,
    Divider,
    Button,
    Chip,
    styled,
    Alert,
    FormControl,
    Select,
    MenuItem,
    TextField,
    IconButton,
    InputLabel,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {
    People,
    EventAvailable,
    Diversity3,
    Refresh,
    TrendingUp,
} from '@mui/icons-material';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import BasicTable from '../../components/Table';
import DateRangeIcon from '@mui/icons-material/DateRange';
import StyledTextField from '../../components/UIComponents/StyledTextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import WorkIcon from '@mui/icons-material/Work';
import StyledAutocomplete from '../../components/UIComponents/StyledAutocomplete';
import CustomCard from '../../components/UIComponents/CustomCard';
import { AuthContext } from '../../context/auth.context';
import { getBranches, getReportData, markAttendance, runReportData, submitAttendance, updateAttendance } from '../../service/network';
import { useLoading } from '../../context/loading.context';
import StatusBadge from '../../components/UIComponents/StatusBadge';
import moment from 'moment';
import { mapKeyValueResponse } from '../../helpers/functions';

// Status Badge Component
const renderStatusBadge = (status) => {
    let color = 'default';
    let label = status;

    switch (status?.toLowerCase()) {
        case 'present':
            color = 'success';
            break;
        case 'absent':
            color = 'error';
            break;
        case 'half day':
            color = 'warning';
            break;
        case 'late':
            color = 'info';
            break;
        default:
            color = 'default';
    }

    return <StatusBadge status={label} color={color} />;
};

// Styled components
const StatCard = styled(Card)(({ theme }) => ({
    borderRadius: '16px',
    boxShadow: 'none',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    height: '100%',
    minHeight: 90,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[6],
        borderColor: theme.palette.primary.main
    }
}));

const StatAvatar = styled(Avatar)(({ theme }) => ({
    width: 48,
    height: 48,
    background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(25,118,210,0.2) 100%)',
    color: theme.palette.primary.main
}));

const getIconforStat = (stat) => {
    switch (stat) {
        case 'Present Records':
            return <EventAvailable sx={{ fontSize: 24 }} color='primary' />
        case 'Half Day Records':
            return <LogoutIcon sx={{ fontSize: 24 }} color='primary' />
        case 'Absent Records':
            return <Diversity3 sx={{ fontSize: 24 }} color='primary' />
        case 'Late Entries':
            return <Diversity3 sx={{ fontSize: 24 }} color='primary' />
        case 'Early Exits':
            return <HistoryToggleOffIcon sx={{ fontSize: 24 }} color='primary' />
        default:
            return <HistoryToggleOffIcon sx={{ fontSize: 24 }} color='primary' />
    }
}

// Cards component
const AttendanceStats = ({ stats }) => {
    return (
        <Grid container spacing={3}>
            {stats?.map((card, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                    <StatCard sx={{ width: '100%', minWidth: 240 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="h5" fontWeight="bold" color={card.indicator}>
                                        {card.value}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {card.label}
                                    </Typography>
                                </Box>
                                <StatAvatar>
                                    {getIconforStat(card.label)}
                                </StatAvatar>
                            </Box>
                        </CardContent>
                    </StatCard>
                </Grid>
            ))}
        </Grid>
    );
};

// Main component
export default function AttendanceDashboard() {
    const [startDate, setStartDate] = useState({
        startDate: moment().subtract(1, 'day').format('YYYY-MM-DD')
    });
    const [data, setData] = useState([]);
    const { selLocation } = useContext(AuthContext);
    const { showLoader, hideLoader } = useLoading();
    const [dateError, setDateError] = useState(null);
    const [reportSummary, setReportSummary] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selBranch, setSelBranch] = useState(null);
    const [count, setCount] = useState({ draft: 0, submitted: 0 });
    const [employeeSearch, setEmployeeSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [filteredData, setFilteredData] = useState([]);
    const [viewMode, setViewMode] = useState('mark'); // 'mark' or 'view'

    useEffect(() => {
        getBranches().then((resp) => {
            let temp = [];
            for (let brn of resp.data.data) {
                temp.push({
                    label: brn.name,
                    value: brn.name
                });
            }
            setBranches(temp);
        }).catch((err) => {
            console.log(err);
        })
    }, [setBranches]);

    // Filter data based on search query and status
    useEffect(() => {
        let filtered = data;

        if (statusFilter === 'Late Entry') {
            filtered = filtered.filter(item => item.late_entry);
        } else if (statusFilter === 'Early Exit') {
            filtered = filtered.filter(item => item.early_exit);
        } else if (statusFilter !== 'All') {
            filtered = filtered.filter(item => item.status === statusFilter);
        }

        const searchTerm = employeeSearch.toLowerCase().trim();
        if (searchTerm) {
            filtered = filtered.filter(item =>
                item.employee_name?.toLowerCase().includes(searchTerm) ||
                item.employee?.toLowerCase().includes(searchTerm)
            );
        }

        setFilteredData(filtered);
    }, [data, employeeSearch, statusFilter]);

    // Fetch attendance data
    const fetchAttendanceData = async () => {
        if (!selLocation[0]) return;

        showLoader();
        try {
            const req_body = {
                doctype: "Attendance",
                fields: ["name", "employee", "employee_name", "shift", "attendance_date", "in_time", "out_time", "late_entry", "early_exit", "status", "working_hours", "docstatus"],
                filters: [["attendance_date", "=", moment(startDate.startDate).format("YYYY-MM-DD")], ["company", "=", selLocation[0]], ["docstatus", "!=", 2]],
                page_length: 10000
            };
            if (selBranch) {
                let emp_body = {
                    doctype: "Employee",
                    fields: ["name"],
                    filters: [["branch", "=", selBranch.value]],
                    page_length: 10000
                }
                let emp_bd = await getReportData(emp_body);
                let emps = mapKeyValueResponse(emp_bd.data.message);
                let emp_list = [];
                for (let ed of emps) {
                    emp_list.push(ed.name);
                }
                req_body.filters.push(["employee", "in", emp_list]);
            }
            const response = await getReportData(req_body);
            let creds = {
                Present: 0,
                Absent: 0,
                "Half Day": 0,
                late_entry: 0,
                early_exit: 0
            };

            let da = mapKeyValueResponse(response.data.message);

            // Attendance has no branch field, so look it up from the Employee records
            const empIds = [...new Set(da.map(d => d.employee).filter(Boolean))];
            if (empIds.length) {
                try {
                    const empResp = await getReportData({
                        doctype: "Employee",
                        fields: ["name", "branch"],
                        filters: [["name", "in", empIds]],
                        page_length: 10000
                    });
                    const branchMap = {};
                    for (const emp of mapKeyValueResponse(empResp.data.message)) {
                        branchMap[emp.name] = emp.branch || '';
                    }
                    da = da.map(d => ({ ...d, branch: branchMap[d.employee] ?? '' }));
                } catch (err) {
                    console.error("Error fetching employee branches:", err);
                }
            }

            let temp = { draft: 0, submitted: 0 };
            for (let dot of da) {
                creds[dot.status] += 1;
                if (dot.early_exit) {
                    creds.early_exit += 1;
                } else if (dot.late_entry) {
                    creds.late_entry += 1;
                }
                if (dot.docstatus == 0) {
                    temp.draft += 1;
                } else if (dot.docstatus == 1) {
                    temp.submitted += 1;
                }
            }
            setCount(temp);
            let report = [
                {
                    "value": creds["Present"],
                    "indicator": "Green",
                    "label": "Present Records",
                    "datatype": "Int"
                },
                {
                    "value": creds["Half Day"],
                    "indicator": "Blue",
                    "label": "Half Day Records",
                    "datatype": "Int"
                },
                {
                    "value": creds["Absent"],
                    "indicator": "Red",
                    "label": "Absent Records",
                    "datatype": "Int"
                },
                {
                    "value": creds["late_entry"],
                    "indicator": "Red",
                    "label": "Late Entries",
                    "datatype": "Int"
                },
                {
                    "value": creds["early_exit"],
                    "indicator": "Red",
                    "label": "Early Exits",
                    "datatype": "Int"
                }
            ];
            setReportSummary(report);
            setData(da);
            setFilteredData(da); // Initialize filtered data with all data
        } catch (error) {
            console.error("Error fetching attendance data:", error);
        } finally {
            hideLoader();
        }
    };

    const handleChange = (field, value) => {
        setStartDate(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Fetch data when date or company changes
    useEffect(() => {
        fetchAttendanceData();
    }, [startDate, selLocation, selBranch]);

    const renderDataTable = (datum) => {
        let temp = [];
        for (let dva in datum) {
            let dta = datum[dva];
            temp.push({
                ...dta,
                working_hours: dta.working_hours ? dta.working_hours.toFixed(2) : '0.00',
                in_time: viewMode === 'mark' ? (
                    <TimePicker
                        disabled={dta.docstatus}
                        value={moment(dta.in_time)}
                        onChange={async (newValue) => {
                            try {
                                showLoader();
                                await updateAttendance(dta.name, { in_time: newValue.format("YYYY-MM-DD HH:mm:ss") });
                                fetchAttendanceData();
                            } catch (err) {
                                console.log(err);
                            } finally {
                                hideLoader();
                            }
                        }}
                        renderInput={(params) => <StyledTextField {...params} fullWidth />}
                    />
                ) : dta.in_time,
                out_time: viewMode === 'mark' ? (
                    <TimePicker
                        disabled={dta.docstatus}
                        value={moment(dta.out_time)}
                        onChange={async (newValue) => {
                            try {
                                showLoader();
                                await updateAttendance(dta.name, { out_time: newValue.format("YYYY-MM-DD HH:mm:ss") });
                                fetchAttendanceData();
                            } catch (err) {
                                console.log(err);
                            } finally {
                                hideLoader();
                            }
                        }}
                        renderInput={(params) => <StyledTextField {...params} fullWidth />}
                    />
                ) : dta.out_time,
                status: viewMode === 'mark' ? (
                    <FormControl fullWidth>
                        <Select
                            labelId="status-select"
                            id="demo-simple-select"
                            value={dta.status}
                            disabled={dta.docstatus}
                            onChange={async (e) => {
                                try {
                                    showLoader();
                                    await updateAttendance(dta.name, { status: e.target.value });
                                    fetchAttendanceData();
                                } catch (err) {
                                    console.log(err);
                                } finally {
                                    hideLoader();
                                }
                            }}
                        >
                            <MenuItem value={"Absent"}>Absent</MenuItem>
                            <MenuItem value={"Half Day"}>Half Day</MenuItem>
                            <MenuItem value={"Present"}>Present</MenuItem>
                        </Select>
                    </FormControl>
                ) : renderStatusBadge(dta.status)
            });
        }
        return temp;
    }

    const tableConfig = {
        rows: [
            {
                id: 'employee',
                label: 'Employee ID',
                accessor: 'employee',
                flex: 1,
                minWidth: '120px',
                align: 'left',
                canSort: true
            },
            {
                id: 'employee_name',
                label: 'Name',
                accessor: 'employee_name',
                flex: 1.5,
                minWidth: '180px',
                align: 'left',
                canSort: true
            },
            {
                id: 'branch',
                label: 'Branch',
                accessor: 'branch',
                flex: 1,
                minWidth: '140px',
                align: 'left',
                canSort: true
            },
            { id: 'shift', label: 'Shift', accessor: 'shift', flex: 1, minWidth: '120px', align: 'center', canSort: false },
            { id: 'checkIn', label: 'Check In', accessor: 'in_time', flex: 1, minWidth: '120px', align: 'center', canSort: false },
            { id: 'checkOut', label: 'Check Out', accessor: 'out_time', flex: 1, minWidth: '120px', align: 'center', canSort: false },
            { id: 'status', label: 'Status', accessor: 'status', flex: 1, minWidth: '120px', align: 'center', canSort: false },
            { id: 'duration', label: 'Duration', accessor: 'working_hours', flex: 1, minWidth: '120px', align: 'center', canSort: false }
        ],
        columns: renderDataTable(filteredData ?? []) || []
    };

    const exportToExcel = () => {
        // 1. Rendered rows hold React elements, so read the raw record and format it
        const getExportValue = (row, accessor) => {
            const value = row[accessor];
            if (accessor === 'in_time' || accessor === 'out_time') {
                if (!value) return '';
                return moment(value).isValid() ? moment(value).format("hh:mm A") : value;
            }
            if (accessor === 'working_hours') {
                return value ? Number(value).toFixed(2) : '0.00';
            }
            return value ?? '';
        };

        // 2. Prepare the data with readable column labels
        const exportData = (filteredData ?? []).map((row) => {
            const formattedRow = {
                Date: row.attendance_date ? moment(row.attendance_date).format("DD-MM-YYYY") : ''
            };
            tableConfig.rows.forEach((header) => {
                formattedRow[header.label] = getExportValue(row, header.accessor);
            });
            return formattedRow;
        });

        // 3. Generate the Excel worksheet
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

        // 4. Download the Excel file
        XLSX.writeFile(workbook, "attendance_report.xlsx");
    };


    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
                <CustomCard
                    bgColor="light"
                    shadow="lg"
                    borderRadius="xxl"
                    sx={{
                        width: '100%',
                        minHeight: 800,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        p: 3
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ marginTop: 3 }}>
                            {/* Title + Button in one row */}
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="subTitle" gutterBottom color="primary.main">
                                    {viewMode === 'mark' ? 'Daily Attendance Submission' : 'Daily Attendance Insights'}
                                </Typography>
                                {viewMode === 'mark' && (
                                    <IconButton
                                        aria-label="recalculate"
                                        onClick={async () => {
                                            try {
                                                showLoader();
                                                await markAttendance(`date=${moment(startDate.startDate).format("YYYY-MM-DD")}`);
                                            } catch (err) {
                                                console.log(err);
                                            } finally {
                                                hideLoader();
                                            }
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                borderRadius: "8px",
                                                textTransform: "none",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <Refresh />
                                            Recalculate
                                        </Button>
                                    </IconButton>
                                )}
                            </Box>

                            {/* Subtitle stays below */}
                            <Typography variant="body1" color="text.secondary" mt={1}>
                                Overview of employee presence and punctuality
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                            {viewMode === 'mark' && (
                                <>
                                    <Grid item xs={12} sm={4} mt={1}>
                                        <Typography variant="h7" fontWeight="300">
                                            Submitted {count.submitted} of {count.submitted + count.draft}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={4} justifyContent={"center"} alignItems={"center"}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                            }}
                                            onClick={async () => {
                                                try {
                                                    showLoader();
                                                    let param = `date=${moment(startDate.startDate).format("YYYY-MM-DD")}`;
                                                    if (selBranch) {
                                                        param += `&branch=${selBranch.value}`
                                                    }
                                                    await submitAttendance(param);
                                                } catch (err) {
                                                    console.log(err);
                                                } finally {
                                                    hideLoader();
                                                    fetchAttendanceData();
                                                }
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={4} justifyContent={"center"} alignItems={"center"}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                    }}
                                    onClick={exportToExcel}
                                >
                                    Export
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4} justifyContent={"center"} alignItems={"center"}>
                                <FormControl fullWidth>
                                    <InputLabel id="view-mode-label">View Mode</InputLabel>
                                    <Select
                                        labelId="view-mode-label"
                                        value={viewMode}
                                        label="View Mode"
                                        onChange={(e) => setViewMode(e.target.value)}
                                    >
                                        <MenuItem value="mark">Mark Attendance</MenuItem>
                                        <MenuItem value="view">View Attendance</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Box>
                    </Box>

                    {/* Search and Filter Section */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={2}>
                            <StyledAutocomplete
                                label="Branch"
                                value={selBranch}
                                onChange={(event, newValue) => {
                                    setSelBranch(newValue);
                                }}
                                options={branches}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <WorkIcon color="action" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <StyledTextField
                                label="Date"
                                type="date"
                                value={startDate.startDate}
                                onChange={(e) => handleChange('startDate', e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <DateRangeIcon color="action" />
                                        </InputAdornment>
                                    )
                                }}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={3}>
                            <StyledTextField
                                label="Search Employee"
                                value={employeeSearch}
                                onChange={(e) => setEmployeeSearch(e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="Search by name or ID"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color="action" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <StyledTextField
                                select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Present">Present</MenuItem>
                                <MenuItem value="Half Day">Half Day</MenuItem>
                                <MenuItem value="Absent">Absent</MenuItem>
                                <MenuItem value="Late Entry">Late Entry</MenuItem>
                                <MenuItem value="Early Exit">Early Exit</MenuItem>
                            </StyledTextField>
                        </Grid>
                    </Grid>

                    {dateError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {dateError}
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <AttendanceStats stats={reportSummary ?? []} />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <BasicTable
                            data={tableConfig}
                            backgroundColor="light.main"
                        />
                    </Box>
                </CustomCard>
            </Box>
        </LocalizationProvider>
    );
}